import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { StatutCommande, statutColors, statutLabels } from '../../../mocks/commandes';
import { api } from '../../../services/api';
import { formatPrice } from '../../../utils/format';

export default function VueGlobaleTab() {
  const navigate = useNavigate();
  const [commandes, setCommandes] = useState<any[]>([]);
  const [stocks, setStocks] = useState<any[]>([]);
  const [financeStats, setFinanceStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('atelier_bassoum_token');
      if (!token) return;
      try {
        const [cmds, stks, fStats] = await Promise.all([
          api.getCommandes(token),
          api.getStocks(token),
          api.getFinanceStats(token)
        ]);
        setCommandes(Array.isArray(cmds) ? cmds : []);
        setStocks(Array.isArray(stks) ? stks : []);
        setFinanceStats(fStats);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const caJour = financeStats?.jour_encaisse || 0;
  const caVentes = financeStats?.jour_total_commandes || 0;
  const commandesEnCours = commandes.filter(c => ['EN_ATTENTE', 'EN_COUPE', 'EN_COUTURE'].includes(c.statut)).length;
  const stocksFaibles = stocks.filter(p => Number(p.quantite_stock) <= Number(p.seuil_alerte)).length;
  const totalCommandes = commandes.length;
  const caTotal = financeStats?.total_entrees || 0;

  // Calcul du CA par mois pour l'année en cours
  const currentYear = new Date().getFullYear();
  const revenusParMois = new Array(12).fill(0);
  commandes.forEach(c => {
    const d = new Date(c.date_commande);
    if (d.getFullYear() === currentYear) {
      revenusParMois[d.getMonth()] += Number(c.acompte || 0);
    }
  });

  const maxRevenu = Math.max(...revenusParMois, 1);
  const moisLabels = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];

  const kpis = [
    { 
      label: 'Encaissé jour', 
      value: formatPrice(caJour), 
      subValue: `Ventes: ${formatPrice(caVentes)}`,
      icon: 'ri-cash-line', 
      color: 'text-foret-600', 
      bg: 'bg-foret-50 border-foret-200' 
    },
    { label: 'Commandes en cours', value: commandesEnCours.toString(), icon: 'ri-time-line', color: 'text-terre-600', bg: 'bg-terre-50 border-terre-200' },
    { label: 'Total commandes', value: totalCommandes.toString(), icon: 'ri-file-list-3-line', color: 'text-or-600', bg: 'bg-or-50 border-or-200' },
    { label: 'Alertes stock', value: stocksFaibles.toString(), icon: 'ri-alert-line', color: 'text-red-600', bg: 'bg-red-50 border-red-200', alert: stocksFaibles > 0 },
  ];

  if (loading) return (
    <div className="flex items-center justify-center py-12">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foret-600"></div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        {kpis.map(k => (
          <div key={k.label} className={`rounded-2xl border p-4 ${k.bg}`}>
            <div className="flex items-start justify-between mb-3">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${k.bg} border ${k.bg.split(' ')[1]}`}>
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${k.icon} ${k.color} text-lg`} />
                </div>
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900 font-nunito">{k.value}</p>
            <div className="flex justify-between items-center mt-0.5">
              <p className="text-xs text-gray-500">{k.label}</p>
              {k.subValue && <p className="text-[10px] font-bold text-foret-500">{k.subValue}</p>}
            </div>
          </div>
        ))}
      </div>

      {/* Chart + Production + Stocks */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        {/* Revenue Chart */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-playfair font-bold text-base text-gray-900">CA mensuel ({currentYear})</h3>
            <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-full">{formatPrice(caTotal)}</span>
          </div>
          <div className="flex items-end gap-1 h-28">
            {revenusParMois.map((v, i) => (
              <div
                key={i}
                className="flex-1 rounded-t-sm transition-all cursor-pointer group relative"
              >
                <div 
                   className={`w-full rounded-t-sm ${i === new Date().getMonth() ? 'bg-foret-600' : 'bg-foret-200'}`}
                   style={{ height: `${(v / maxRevenu) * 100}%` }}
                   aria-label={`${moisLabels[i]} : ${formatPrice(v, false)}`}
                />
                <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-[10px] rounded px-1.5 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-20">
                  {moisLabels[i]}: {formatPrice(v, false)}
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-2">
            {moisLabels.map(m => <span key={m} className="text-xs text-gray-400 flex-1 text-center">{m}</span>)}
          </div>
        </div>

        {/* État de production */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <h3 className="font-playfair font-bold text-base text-gray-900 mb-4">État de production</h3>
          <div className="space-y-3">
            {(['EN_ATTENTE', 'EN_COUPE', 'EN_COUTURE', 'TERMINE', 'LIVRE'] as const).map(s => {
              const count = commandes.filter(c => c.statut === s).length;
              const colors: Record<string, string> = { EN_ATTENTE: 'bg-yellow-400', EN_COUPE: 'bg-blue-400', EN_COUTURE: 'bg-purple-400', TERMINE: 'bg-foret-400', LIVRE: 'bg-gray-300' };
              return (
                <div key={s}>
                  <div className="flex justify-between text-xs text-gray-600 mb-1">
                    <span>{statutLabels[s]}</span>
                    <span className="font-bold">{count}</span>
                  </div>
                  <div 
                    className="h-2 bg-gray-100 rounded-full overflow-hidden" 
                    title={`${statutLabels[s]} : ${totalCommandes > 0 ? Math.round((count / totalCommandes) * 100) : 0}%`}
                  >
                    {(() => {
                      const perc = totalCommandes > 0 ? (count / totalCommandes) * 100 : 0;
                      const valNow = Math.round(perc);
                      return (
                        <div 
                           className={`h-full rounded-full ${colors[s]}`} 
                           style={{ width: `${perc}%` }}
                           role="progressbar"
                           aria-valuenow={valNow}
                           aria-valuemin={0}
                           aria-valuemax={100}
                           aria-label={statutLabels[s]}
                        />
                      );
                    })()}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Alertes stocks */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-playfair font-bold text-base text-gray-900">Alertes stock</h3>
            {stocksFaibles > 0 && (
              <span className="text-xs bg-red-100 text-red-700 font-semibold px-2 py-0.5 rounded-full">{stocksFaibles} urgent{stocksFaibles > 1 ? 's' : ''}</span>
            )}
          </div>
          <div className="space-y-2">
            {stocks.filter(p => Number(p.quantite_stock) <= Number(p.seuil_alerte)).slice(0, 4).map(p => (
              <div key={p.id} className="flex items-center gap-3 bg-red-50 rounded-xl px-3 py-2">
                <div className="w-5 h-5 flex items-center justify-center text-red-500 flex-shrink-0">
                  <i className="ri-alert-line text-sm" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-gray-800 truncate">{p.nom}</p>
                  <p className="text-xs text-red-600">{p.quantite_stock} {p.unite} / seuil {p.seuil_alerte}</p>
                </div>
              </div>
            ))}
            {stocksFaibles === 0 && (
              <div className="text-center py-6 text-gray-400">
                <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                  <i className="ri-checkbox-circle-line text-2xl text-foret-400" />
                </div>
                <p className="text-xs">Tous les stocks sont OK</p>
              </div>
            )}
            <button onClick={() => navigate('/stocks')} className="w-full text-xs text-foret-600 font-semibold py-2 rounded-xl border border-foret-200 hover:bg-foret-50 cursor-pointer transition-colors whitespace-nowrap">
              Gérer les stocks →
            </button>
          </div>
        </div>
      </div>

      {/* Recent orders */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-playfair font-bold text-base text-gray-900">Commandes récentes</h3>
          <button onClick={() => navigate('/commande/nouvelle')} className="text-xs bg-foret-500 text-white px-3 py-1.5 rounded-full font-semibold cursor-pointer hover:bg-foret-600 whitespace-nowrap">
            + Nouvelle
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                {['Ref', 'Client', 'Modèle', 'Livraison', 'Montant', 'Statut'].map(h => (
                  <th key={h} className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider pb-3 pr-4">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {commandes.slice(0, 5).map(c => (
                <tr key={c.id} className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer transition-colors" onClick={() => navigate(`/commande/${c.id}`)}>
                  <td className="py-3 pr-4">
                    <p className="text-xs font-mono text-foret-600 font-semibold leading-none">{c.code}</p>
                    <p className="text-[9px] text-gray-400 mt-1 font-bold">
                      {new Date(c.date_commande).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })} · {new Date(c.date_commande).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </td>
                  <td className="py-3 pr-4 text-sm font-medium text-gray-800">{c.client_nom}</td>
                  <td className="py-3 pr-4 text-xs text-gray-500 truncate max-w-32">{c.modele_nom || 'Sur mesure'}</td>
                  <td className="py-3 pr-4 text-xs text-gray-500 font-bold">{c.date_livraison ? new Date(c.date_livraison).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : '-'}</td>
                  <td className="py-3 pr-4 text-xs font-semibold text-gray-700">{formatPrice(c.total, false)}</td>
                  <td className="py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${statutColors[c.statut as StatutCommande] || 'bg-gray-100'}`}>{statutLabels[c.statut as StatutCommande] || c.statut}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
